import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Stamp, Printer, Sparkles,
  AlertTriangle, FileSignature, Lock, Eye, Download,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';
import { parseMedicare, formatDigitsDisplay, formatMedicare } from '../lib/medicare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.consent;

// Preview/print styles shared between the live preview and the printed PDF.
// The content is identical so 'what you see is what you print'.
const PRINT_STYLES = `
  .consent-doc {
    max-width: 820px; margin: 0 auto;
    background: white; color: #111; font-family: 'Museo Sans','Nunito Sans','Inter',sans-serif;
    padding: 36px 44px; border: 1px solid #e5e7eb; border-radius: 8px;
  }
  .consent-doc .legal-header { border-bottom: 3px solid #1f4a2e; padding-bottom: 14px; margin-bottom: 22px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  .consent-doc .legal-header .brand-left { display: flex; align-items: flex-start; gap: 14px; }
  .consent-doc .legal-header .brand-left img { width: 54px; height: 54px; object-fit: contain; flex: none; }
  .consent-doc .legal-title { font-family: 'Museo Slab','Zilla Slab','Outfit',serif; font-size: 22px; font-weight: 400; letter-spacing: -0.01em; line-height: 1.2; color: #1f4a2e; }
  .consent-doc .legal-meta { text-align: right; font-size: 10px; color: #6b7280; font-variant-numeric: tabular-nums; }
  .consent-doc h2 { font-family: 'Museo Slab','Zilla Slab','Outfit',serif; font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #1f4a2e; border-bottom: 2px solid #3fa758; padding-bottom: 4px; margin-top: 22px; margin-bottom: 10px; }
  .consent-doc p, .consent-doc li { font-size: 12.5px; line-height: 1.55; color: #111; }
  .consent-doc ul { padding-left: 20px; margin: 8px 0; }
  .consent-doc li { margin-bottom: 3px; list-style: disc; }
  .consent-doc .declaration { border: 2px solid #1f4a2e; border-radius: 6px; padding: 14px 18px; margin-top: 20px; background: #e8fafb; }
  .consent-doc .declaration ul { list-style: none; padding-left: 0; }
  .consent-doc .declaration li { position: relative; padding-left: 22px; margin-bottom: 6px; list-style: none; }
  .consent-doc .declaration li:before { content: '☐'; position: absolute; left: 0; top: -1px; font-size: 14px; color: #1f4a2e; }
  .consent-doc .declaration li.checked:before { content: '☑'; color: #3fa758; }
  .consent-doc .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 28px; padding-top: 18px; border-top: 1px solid #e5e7eb; }
  .consent-doc .sig-box { border-bottom: 1.5px solid #1f4a2e; padding-bottom: 6px; min-height: 36px; font-family: 'Caveat', cursive; font-size: 26px; color: #1f4a2e; }
  .consent-doc .sig-label { font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #6b7280; margin-top: 4px; }
  .consent-doc .small { font-size: 10px; color: #6b7280; }
  .consent-doc .pt-field { border-bottom: 1px solid #9ca3af; padding: 2px 4px; min-height: 20px; font-size: 13px; }
  .consent-doc .pt-row { display: grid; grid-template-columns: 160px 1fr; gap: 8px; align-items: end; margin-bottom: 8px; }
  .consent-doc .pt-row .pt-label { font-size: 11px; font-weight: 600; color: #374151; }
  .consent-doc .pt-box-wrap .pt-label { display: block; font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 3px; }
  .consent-doc .pt-box { border: 1px solid #9ca3af; border-radius: 4px; padding: 0 6px; font-size: 13px; line-height: 20px; min-height: 122px; white-space: pre-wrap; overflow-wrap: anywhere;
    background-image: repeating-linear-gradient(to bottom, transparent 0, transparent 19px, #e5e7eb 19px, #e5e7eb 20px); background-position: 0 1px;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .consent-doc .pt-box.pt-box-sm { min-height: 62px; }
  .consent-doc .pt-box-wrap { margin-bottom: 6px; }
  .consent-doc .pt-yn { font-weight: 400; margin-left: 8px; font-size: 12px; }
  .consent-doc .declare-line { font-weight: 600; color: #1f4a2e; font-size: 13.5px; margin-top: 12px; padding-top: 10px; border-top: 1px dashed #9ca3af; }
  .consent-doc .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.08em; }
  .consent-doc .brand-rule { height: 4px; background: linear-gradient(90deg, #1f4a2e 0%, #3fa758 100%); margin-bottom: 18px; border-radius: 2px; }
  .consent-doc .warning-box { background: #FEF2F2; border: 1px solid #FCA5A5; border-radius: 6px; padding: 10px 14px; margin: 8px 0; }
  /* Two-column packing for patient details + health declarations on screen
     when there's space (≥720px). Print enforces it unconditionally below. */
  .consent-doc .pt-cols { display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; row-gap: 0; }
  @media (max-width: 720px) {
    .consent-doc .pt-cols { display: block; }
  }
  @media print {
    body { background: white !important; }
    .consent-doc {
      box-shadow: none !important; border: none !important;
      padding: 0 !important; max-width: none !important;
      /* 2-page A4 max: text sized so the longest template + 6-line boxes fit. */
      font-size: 13px;
    }
    .consent-doc p, .consent-doc li { font-size: 13px !important; line-height: 1.34 !important; margin: 0 0 3px 0 !important; }
    .consent-doc ul.cols-2 { columns: 2; column-gap: 22px; }
    .consent-doc ul.cols-2 li { break-inside: avoid; }
    .consent-doc ul { margin: 5px 0 !important; padding-left: 18px !important; }
    .consent-doc li { margin-bottom: 2.5px !important; }
    .consent-doc h2 { font-size: 14.5px !important; margin-top: 10px !important; margin-bottom: 5px !important; padding-bottom: 2px !important; }
    .consent-doc .legal-header { padding-bottom: 6px !important; margin-bottom: 8px !important; }
    .consent-doc .legal-header .brand-left img { width: 44px !important; height: 44px !important; }
    .consent-doc .legal-title { font-size: 20px !important; line-height: 1.2 !important; }
    .consent-doc .pt-row { grid-template-columns: 115px 1fr !important; margin-bottom: 3px !important; }
    .consent-doc .pt-row .pt-label { font-size: 13px !important; }
    .consent-doc .pt-box-wrap .pt-label { font-size: 13px !important; margin-bottom: 2px !important; }
    .consent-doc .pt-box { font-size: 13.5px !important; line-height: 19px !important; min-height: 116px !important;
      background-image: repeating-linear-gradient(to bottom, transparent 0, transparent 18px, #e5e7eb 18px, #e5e7eb 19px) !important; }
    .consent-doc .pt-box.pt-box-sm { min-height: 59px !important; }
    .consent-doc .pt-yn { font-size: 13.5px !important; }
    .consent-doc .pt-field { font-size: 14.5px !important; min-height: 18px !important; padding: 1px 5px !important; }
    .consent-doc .pt-cols { display: grid !important; grid-template-columns: 1fr 1fr !important; column-gap: 20px !important; row-gap: 0 !important; }
    .consent-doc .declaration { padding: 8px 13px !important; margin-top: 8px !important; }
    .consent-doc .declaration li { padding-left: 22px !important; margin-bottom: 2.5px !important; list-style: none !important; }
    .consent-doc .declaration li:before { font-size: 16px !important; }
    .consent-doc .declare-line { font-size: 13px !important; margin-top: 6px !important; padding-top: 5px !important; }
    .consent-doc .sig-grid { gap: 14px !important; margin-top: 10px !important; padding-top: 7px !important; }
    .consent-doc .sig-box { font-size: 22px !important; min-height: 28px !important; padding-bottom: 3px !important; }
    .consent-doc .sig-label { font-size: 10px !important; }
    .consent-doc .footer { margin-top: 8px !important; padding-top: 4px !important; font-size: 10px !important; }
    .consent-doc .brand-rule { display: none !important; }
    .consent-doc .legal-header .small,
    .consent-doc .legal-meta { font-size: 11.5px !important; }
    .print-hidden, [data-testid="brand-footer"], [data-testid="chat-bubble-btn"], [data-testid="chat-panel"] { display: none !important; }
    .min-h-screen { min-height: 0 !important; }
    .consent-form-wrap { padding: 0 !important; max-width: none !important; }
    .consent-form-grid { display: block !important; grid-template-columns: 1fr !important; }
    /* Avoid awkward page-break inside critical blocks */
    .consent-doc h2 { page-break-after: avoid; }
    .consent-doc .declaration li, .consent-doc .pt-cols, .consent-doc .sig-grid { page-break-inside: avoid; }
    .consent-doc .biopsy-site-addendum { background: white !important; border-left: none !important; padding: 0 !important; margin-top: 5px !important; }
    @page { size: A4; margin: 9mm 10mm 9mm 10mm; }
  }
`;

// ────────────────────────────────────────────────────────────────────────────
// Index — tile grid of 8 consent forms + recent submissions link for PMs
// ────────────────────────────────────────────────────────────────────────────
export function ConsentIndexPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/consent-templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="consent-index">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" style={{ color: C.color }} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: C.color }}>Image-guided procedure consent</span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Consent <span style={{ color: C.color }}>forms</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Australian <strong>Montgomery-standard</strong> consent forms. Each form walks the patient through the procedure, risks, alternatives, and a procedure-specific declaration. Complete on the tablet, print, or export as PDF.
            </p>
          </div>
          <a
            href={`${API}/consent-templates/download-all.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="consent-download-all"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border shrink-0"
            style={{ borderColor: `${C.color}40`, color: C.color }}
          >
            <Download className="w-4 h-4" /> Download all (PDF)
          </a>
        </div>

        {isPM && (
          <Link to="/management" data-testid="consent-admin-link"
            className="flex items-center justify-between p-4 bg-card border border-border rounded-xl mb-8 hover:border-[--c]"
            style={{ '--c': C.color }}>
            <div>
              <p className="text-sm font-semibold text-foreground">Consent submissions dashboard</p>
              <p className="text-[11px] text-muted-foreground">Review, countersign and archive completed consents from /management.</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        )}

        {loading ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="consent-templates-list">
            {templates.map(t => (
              <Link
                key={t.template_id}
                to={`/consent/${t.template_id}`}
                data-testid={`consent-template-${t.template_id}`}
                className="group p-5 bg-card border border-border rounded-2xl hover:border-[--c] hover:shadow-sm transition-all"
                style={{ '--c': C.color, borderLeftWidth: 3, borderLeftColor: C.color }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>{t.procedure_type}</p>
                    <h3 className="font-['Outfit'] text-base font-bold text-foreground mt-1">{t.title}</h3>
                    <p className="text-[11px] text-muted-foreground mt-1">v{t.version}
                      {t.requires_driver && <span> · Driver required</span>}
                      {t.uses_steroid && <span> · Steroid</span>}
                      {t.requires_claustrophobia_check && <span> · CT</span>}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Fill-in / live-preview page. Left column = inputs. Right column = the
// document exactly as it will print. Updates in real time.
// ────────────────────────────────────────────────────────────────────────────
export function ConsentFormPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const [patient, setPatient] = useState({ name: '', dob: '', phone: '' });
  const [btStatus, setBtStatus] = useState('none');
  const [btCeased, setBtCeased] = useState('');
  const [allergies, setAllergies] = useState('');
  const [medications, setMedications] = useState('');
  const [surgeries, setSurgeries] = useState('');
  const [diabetic, setDiabetic] = useState(null);
  const [diabeticDetails, setDiabeticDetails] = useState('');
  const [driverBrought, setDriverBrought] = useState(null);
  const [covid2w, setCovid2w] = useState(null);
  const [covidNext, setCovidNext] = useState(null);
  const [claustro, setClaustro] = useState(null);
  const [procedureSite, setProcedureSite] = useState('');
  const [ticks, setTicks] = useState({});
  const [universal, setUniversal] = useState(false);
  const [signature, setSignature] = useState('');
  const [radName, setRadName] = useState('');

  useEffect(() => {
    axios.get(`${API}/consent-templates/${templateId}`, { withCredentials: true })
      .then(r => setTemplate(r.data))
      .catch(() => setErr('Template not found'))
      .finally(() => setLoading(false));
  }, [templateId]);

  if (loading) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">Loading…</p></div>;
  if (!template) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">{err}</p></div>;

  const allTicked = (template.consent_statement || []).every(c => ticks[c.id]);
  const canSubmit = patient.name.trim() && universal && signature.trim() && allTicked
    && (!template.requires_driver || driverBrought !== null)
    && (!template.uses_steroid || (covid2w !== null && covidNext !== null))
    && (!(template.site_options || []).length || !!procedureSite);

  const submit = async () => {
    setErr(''); setSubmitting(true);
    try {
      const r = await axios.post(`${API}/consents`, {
        template_id: templateId,
        patient,
        blood_thinner_status: btStatus,
        blood_thinner_ceased_date: btCeased,
        allergies, medications,
        previous_surgeries: surgeries,
        diabetic,
        diabetic_details: diabeticDetails,
        driver_brought: driverBrought,
        covid_vaccine_last_2_weeks: covid2w,
        covid_vaccine_next_week: covidNext,
        claustrophobic: claustro,
        procedure_site: procedureSite,
        consent_ticks: ticks,
        universal_confirmation: universal,
        typed_signature: signature,
        radiologist_name: radName,
      }, { withCredentials: true });
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background" data-testid="consent-submitted">
        <Header />
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6" style={{ backgroundColor: `${C.color}15` }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: C.color }} />
          </div>
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">Consent recorded.</h1>
          <p className="mt-2 text-sm text-muted-foreground">A time-stamped copy has been saved. Print for the patient file or hand them a PDF.</p>
          <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
            <Link to={`/consent/submission/${done.submission_id}`} data-testid="consent-done-view"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: C.color }}>
              <Printer className="w-4 h-4" /> View & print PDF
            </Link>
            <Link to="/consent" data-testid="consent-done-back"
              className="text-sm font-semibold px-4 py-2 rounded-lg border border-border">Back to consent library</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="consent-form-page">
      <div className="print-hidden"><Header /></div>
      <div className="consent-form-wrap max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="print-hidden flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button onClick={() => navigate('/consent')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to consent library
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="consent-print-capture"
              title="Print the form on the right. Leave the left blank to print a paper-fill copy for the patient, or fill on screen first and print the completed PDF for RIS."
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border"
              style={{ borderColor: `${C.color}40`, color: C.color }}>
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>
        </div>

        <div className="print-hidden rounded-xl border border-dashed p-3 mb-4 text-[11px] text-muted-foreground flex items-start gap-2"
          style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}06` }}
          data-testid="consent-print-hint">
          <Printer className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: C.color }} />
          <span>
            <strong style={{ color: C.color }}>Need a paper copy?</strong> Click <strong>Print / Save as PDF</strong> above — it prints only the document on the right. Leave the fields empty for a paper-fill copy the patient can complete by hand, or fill on screen first to print the completed form for scanning into the RIS.
          </span>
        </div>

        <div className="consent-form-grid grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* Left: Inputs */}
          <div className="space-y-4 print-hidden" data-testid="consent-inputs">
            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Patient details</p>
              <div className="space-y-2 mt-2">
                <Field label="Full name *" value={patient.name} onChange={v => setPatient({ ...patient, name: v })} testid="c-patient-name" />
                <Field label="Date of birth" value={patient.dob} onChange={v => setPatient({ ...patient, dob: v })} testid="c-patient-dob" placeholder="dd/mm/yyyy" />
                <Field label="Phone" value={patient.phone} onChange={v => setPatient({ ...patient, phone: v })} testid="c-patient-phone" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Blood thinners</p>
              <div className="space-y-2 mt-2" data-testid="c-blood-thinner">
                {[
                  { v: 'none',         label: 'I do not take any blood thinning medication' },
                  { v: 'ceased',       label: 'I have ceased my blood thinning medication' },
                  { v: 'still_taking', label: 'I am still taking my blood thinning medication' },
                ].map(o => (
                  <RadioRow key={o.v} checked={btStatus === o.v} onChange={() => setBtStatus(o.v)}
                    testid={`c-bt-${o.v}`} label={o.label} warn={o.v === 'still_taking'} />
                ))}
                {btStatus === 'ceased' && (
                  <Field label="Date ceased" value={btCeased} onChange={setBtCeased} testid="c-bt-ceased-date" placeholder="dd/mm/yyyy" />
                )}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4">
              <TextArea label="Allergies" value={allergies} onChange={setAllergies} testid="c-allergies" placeholder="List any allergies, or write 'nil'" rows={3} />
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Diabetes</p>
              <YesNoRow label="Are you a diabetic?" value={diabetic} onChange={setDiabetic} testid="c-diabetic" />
              {diabetic === true && (
                <TextArea label="Diabetes details" value={diabeticDetails} onChange={setDiabeticDetails} testid="c-diabetic-details" placeholder="Type 1 / Type 2, insulin or tablets, last reading" rows={2} />
              )}
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
              <TextArea label="Current medications" value={medications} onChange={setMedications} testid="c-medications" placeholder="Medication and what it's for (e.g. Metformin for diabetes)" rows={6} />
              <TextArea label="Previous surgeries" value={surgeries} onChange={setSurgeries} testid="c-surgeries" placeholder="Operation and approximate year (e.g. Knee arthroscopy 2019)" rows={6} />
            </div>

            {template.requires_driver && (
              <div className="bg-card border border-border rounded-2xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Spinal procedures — driver</p>
                <p className="text-[11px] text-muted-foreground mt-1">You have been advised to bring a driver due to the intensity of this procedure.</p>
                <div className="flex gap-2 mt-2" data-testid="c-driver">
                  <PillBtn active={driverBrought === true} onClick={() => setDriverBrought(true)} testid="c-driver-yes">Yes, driver arranged</PillBtn>
                  <PillBtn active={driverBrought === false} onClick={() => setDriverBrought(false)} testid="c-driver-no" warn>No driver</PillBtn>
                </div>
              </div>
            )}

            {template.uses_steroid && (
              <div className="bg-card border border-border rounded-2xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>COVID vaccine</p>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{template.covid_blurb}</p>
                <div className="mt-3 space-y-2" data-testid="c-covid">
                  <YesNoRow label="COVID vaccine in the last 2 weeks?" value={covid2w} onChange={setCovid2w} testid="c-covid-2w" />
                  <YesNoRow label="Planning COVID vaccine in the next week?" value={covidNext} onChange={setCovidNext} testid="c-covid-next" />
                </div>
              </div>
            )}

            {template.requires_claustrophobia_check && (
              <div className="bg-card border border-border rounded-2xl p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Claustrophobia</p>
                <p className="text-[11px] text-muted-foreground mt-1">CT-guided procedures only.</p>
                <YesNoRow label="Are you claustrophobic?" value={claustro} onChange={setClaustro} testid="c-claustro" />
              </div>
            )}

            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Patient declaration — tick every box</p>
              <ul className="space-y-2 mt-2" data-testid="c-declaration">
                {(template.consent_statement || []).map(c => (
                  <li key={c.id}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!ticks[c.id]}
                        onChange={e => setTicks({ ...ticks, [c.id]: e.target.checked })}
                        data-testid={`c-tick-${c.id}`}
                        className="w-4 h-4 mt-0.5 rounded border-border" />
                      <span className="text-xs text-foreground">{c.text}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <label className="flex items-start gap-2 mt-4 pt-3 border-t border-border cursor-pointer">
                <input type="checkbox" checked={universal} onChange={e => setUniversal(e.target.checked)}
                  data-testid="c-universal"
                  className="w-4 h-4 mt-0.5 rounded border-border" />
                <span className="text-xs font-semibold text-foreground">{template.universal_confirmation}</span>
              </label>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
              <Field label="Patient signature (typed full name) *" value={signature} onChange={setSignature} testid="c-signature" />
              <Field label="Witnessing radiologist (optional at capture)" value={radName} onChange={setRadName} testid="c-radiologist" />
            </div>

            {err && <p className="text-xs text-red-500" data-testid="c-error">{err}</p>}

            <div className="flex items-center justify-end gap-2 sticky bottom-4">
              <button onClick={submit} disabled={!canSubmit || submitting}
                data-testid="c-submit"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-3 rounded-xl text-white disabled:opacity-40 shadow-lg"
                style={{ backgroundColor: C.color }}>
                <CheckCircle2 className="w-4 h-4" /> {submitting ? 'Saving…' : 'Record consent'}
              </button>
            </div>
          </div>

          {/* Right: Live document preview */}
          <div className="overflow-auto" data-testid="consent-preview-pane">
            <style>{PRINT_STYLES}</style>
            <ConsentDoc
              template={template}
              patient={patient}
              btStatus={btStatus} btCeased={btCeased}
              allergies={allergies} medications={medications} surgeries={surgeries}
              diabetic={diabetic} diabeticDetails={diabeticDetails}
              driverBrought={driverBrought}
              covid2w={covid2w} covidNext={covidNext}
              claustro={claustro}
              procedureSite={procedureSite} setProcedureSite={setProcedureSite}
              ticks={ticks} universal={universal}
              signature={signature} radName={radName}
              signedAt={null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// The actual legal document layout, used both for live preview and print.
// ────────────────────────────────────────────────────────────────────────────
function ConsentDoc({ template, patient, btStatus, btCeased, allergies, medications, surgeries,
                     diabetic, diabeticDetails,
                     driverBrought, covid2w, covidNext, claustro,
                     procedureSite, setProcedureSite,
                     ticks, universal, signature, radName,
                     signedAt, countersignedAt, countersignedBy }) {
  const issued = signedAt ? new Date(signedAt).toLocaleString('en-AU') : 'Draft — live preview';

  return (
    <div className="consent-doc" data-testid="consent-doc">
      <div className="brand-rule" />
      {/* Header */}
      <div className="legal-header">
        <div className="brand-left">
          <img src="/brand/sp-logo.png" alt="Sanctuary Point Ultrasound" style={{ height: 60, width: 'auto', objectFit: 'contain' }} />
          <div>
            <p className="small" style={{ color: '#1f4a2e', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 8 }}>
              Sanctuary Point Ultrasound
            </p>
            <h1 className="legal-title">{template.title}</h1>
            <p className="small" style={{ marginTop: 4, fontStyle: 'italic' }}>Image-Guided Procedure · Consent &amp; Patient Declaration</p>
          </div>
        </div>
        <div className="legal-meta">
          <div>Version {template.version}</div>
          <div>{issued}</div>
        </div>
      </div>

      {/* Introduction */}
      <h2>1 · The procedure</h2>
      <p><strong>Your doctor has requested an image-guided procedure: {template.procedure_type}.</strong></p>
      <p style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{template.procedure_description}</p>

      {/* Site picker (screen only, draft/editing mode) + selected-site
          addendum (screen + print, persistent on submitted view). */}
      {(template.site_options || []).length > 0 && (
        <>
          {!signedAt && (
            <div className="print-hidden" style={{ marginTop: 10, padding: '8px 12px', background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: 6 }}
                 data-testid="biopsy-site-picker-wrap">
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#1f4a2e', marginBottom: 4 }} htmlFor="biopsy-site">
                Biopsy site *
              </label>
              <select
                id="biopsy-site"
                value={procedureSite}
                onChange={e => setProcedureSite(e.target.value)}
                data-testid="biopsy-site-select"
                style={{ fontSize: 13, padding: '5px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: 'white', minWidth: 280 }}
              >
                <option value="">— Select site —</option>
                {template.site_options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                Site-specific risks shown below are added to the printed consent.
              </p>
            </div>
          )}
          {procedureSite && (() => {
            const opt = (template.site_options || []).find(o => o.value === procedureSite);
            if (!opt) return null;
            return (
              <p data-testid="biopsy-site-addendum"
                 className="biopsy-site-addendum"
                 style={{ marginTop: 8, padding: '6px 10px', background: '#fef9c3', borderLeft: '3px solid #ca8a04', borderRadius: 3 }}>
                <strong>Biopsy site — {opt.label}.</strong> {opt.risk_addendum}
              </p>
            );
          })()}
        </>
      )}

      <h2>2 · Purpose</h2>
      <ul>
        {(template.purpose || []).map((p, i) => <li key={i}>{p}</li>)}
      </ul>

      {template.biopsy_post && <p style={{ marginTop: 8 }}>{template.biopsy_post}</p>}

      {(template.pre_procedure_safety || []).length > 0 && (
        <>
          <h2>Pre-procedure safety checklist</h2>
          <ul>
            {template.pre_procedure_safety.map((s, i) => <li key={`pps-${i}`}>{s}</li>)}
          </ul>
          <p style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
            <em>Aligned with Australian best-practice (eTG · RANZCR · ASUM · ANZCA PS09).</em>
          </p>
          {template.anticoag_note && (
            <p style={{ marginTop: 6 }}><strong>Blood thinners:</strong> {template.anticoag_note}</p>
          )}
          {template.diabetes_note && (
            <p style={{ marginTop: 6 }}><strong>Diabetes:</strong> {template.diabetes_note}</p>
          )}
        </>
      )}

      {/* Post-procedure care — delivered separately via the Patient Information
          Leaflet (see /pils). A short reference here closes the documentation
          chain without duplicating the PIL content on this 2-page form. */}
      {template.pil_id ? (
        <p style={{ marginTop: 6, fontSize: '0.95em' }}>
          <strong>Post-procedure care:</strong> a dedicated Patient Information Leaflet covering
          what to expect, when to seek help, and follow-up advice has been
          provided to the patient separately and forms part of this consent.
        </p>
      ) : (
        <>
          <h2>3 · Post-procedure — when to seek help</h2>
          <p>{template.infection_note}</p>
          <ul className="cols-2">
            {(template.infection_signs || []).map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <p><em>The above symptoms may be signs of infection and need to be treated as soon as possible.</em></p>
        </>
      )}

      {/* Patient details */}
      <h2>{template.pil_id ? '3' : '4'} · Patient details</h2>
      <div className="pt-cols">
        <div className="pt-row"><span className="pt-label">Full name</span><span className="pt-field">{patient.name || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Date of birth</span><span className="pt-field">{patient.dob || '\u00A0'}</span></div>
        <div className="pt-row"><span className="pt-label">Phone</span><span className="pt-field">{patient.phone || '\u00A0'}</span></div>
      </div>

      {/* Health declarations */}
      <h2>{template.pil_id ? '4' : '5'} · Health declarations</h2>
      <ul className="declaration-list">
        <li>Blood thinning medication: <strong>
          {btStatus === 'none' && 'I do not take any blood thinning medication.'}
          {btStatus === 'ceased' && `Ceased${btCeased ? ` on ${btCeased}` : ''}.`}
          {btStatus === 'still_taking' && 'I am still taking my blood thinning medication.'}
        </strong></li>
      </ul>
      <div className="pt-cols" style={{ marginTop: 6 }}>
        <div className="pt-box-wrap">
          <span className="pt-label">Allergies</span>
          <div className="pt-box pt-box-sm" data-testid="consent-doc-allergies">{allergies || '\u00A0'}</div>
        </div>
        <div className="pt-box-wrap">
          <span className="pt-label">
            Are you a diabetic?
            <span className="pt-yn" data-testid="consent-doc-diabetic">
              {diabetic === true ? '☑' : '☐'} Yes &nbsp; {diabetic === false ? '☑' : '☐'} No
            </span>
          </span>
          <div className="pt-box pt-box-sm" data-testid="consent-doc-diabetic-details">{diabeticDetails || '\u00A0'}</div>
        </div>
      </div>
      <div className="pt-cols">
        <div className="pt-box-wrap">
          <span className="pt-label">Current medications</span>
          <div className="pt-box" data-testid="consent-doc-medications">{medications || '\u00A0'}</div>
        </div>
        <div className="pt-box-wrap">
          <span className="pt-label">Previous surgeries</span>
          <div className="pt-box" data-testid="consent-doc-surgeries">{surgeries || '\u00A0'}</div>
        </div>
      </div>
      <div className="pt-cols">
        {template.requires_driver && (
          <div className="pt-row"><span className="pt-label">Driver arranged</span><span className="pt-field">
            {driverBrought === true ? 'Yes — driver present' : driverBrought === false ? 'No driver' : '\u00A0'}
          </span></div>
        )}
        {template.requires_claustrophobia_check && (
          <div className="pt-row"><span className="pt-label">Claustrophobic</span><span className="pt-field">
            {claustro === true ? 'Yes' : claustro === false ? 'No' : '\u00A0'}
          </span></div>
        )}
        {template.uses_steroid && (
          <>
            <div className="pt-row"><span className="pt-label">COVID vaccine (last 2 weeks)</span><span className="pt-field">
              {covid2w === true ? 'Yes' : covid2w === false ? 'No' : '\u00A0'}
            </span></div>
            <div className="pt-row"><span className="pt-label">COVID vaccine (next week)</span><span className="pt-field">
              {covidNext === true ? 'Yes' : covidNext === false ? 'No' : '\u00A0'}
            </span></div>
          </>
        )}
      </div>
      {template.uses_steroid && (
        <p style={{ marginTop: 4, fontSize: 10, fontStyle: 'italic', color: '#374151' }}>{template.covid_blurb}</p>
      )}

      {/* Consent declaration */}
      <h2>{template.pil_id ? '5' : '6'} · Consent declaration</h2>
      <p style={{ marginBottom: 8 }}>I confirm that:</p>
      <div className="declaration">
        <ul>
          {(template.consent_statement || []).map(c => (
            <li key={c.id} className={ticks[c.id] ? 'checked' : ''}>{c.text}</li>
          ))}
        </ul>
        <p className="declare-line">{template.voluntary_line}</p>
        <p style={{ marginTop: 10, fontWeight: 600, fontSize: 12.5 }}>
          <span style={{ marginRight: 6, color: C.color }}>{universal ? '☑' : '☐'}</span>
          {template.universal_confirmation}
        </p>
      </div>

      {template.time_out_note && (
        <>
          <h2>Time-out (immediately before the procedure)</h2>
          <p>{template.time_out_note}</p>
        </>
      )}

      {template.pil_id && (
        <p className="print-hidden" style={{ marginTop: 12, padding: '8px 12px', background: '#eef5f0', border: '1px solid #3fa758', borderRadius: 4, fontSize: 11.5 }}
           data-testid="consent-pil-link">
          <strong style={{ color: '#1f4a2e' }}>Take-home information sheet:</strong>{' '}
          a post-procedure Patient Information Leaflet (<code>{template.pil_id}</code>) will be
          issued to the patient at discharge.{' '}
          <a href={`${process.env.REACT_APP_BACKEND_URL}${template.pil_pdf_url}`}
             target="_blank" rel="noopener noreferrer"
             style={{ color: '#1f4a2e', textDecoration: 'underline' }}
             data-testid="consent-pil-pdf-link">
            Download PIL (PDF)
          </a>
        </p>
      )}

      {/* Signature */}
      <div className="sig-grid">
        <div>
          <div className="sig-box">{signature || '\u00A0'}</div>
          <div className="sig-label">Patient signature</div>
          <div className="small" style={{ marginTop: 2 }}>{signedAt ? new Date(signedAt).toLocaleString('en-AU') : 'Date: __________________'}</div>
        </div>
        <div>
          <div className="sig-box">{(countersignedBy?.name || radName) || '\u00A0'}</div>
          <div className="sig-label">Radiologist / clinician (witness)</div>
          <div className="small" style={{ marginTop: 2 }}>{countersignedAt ? new Date(countersignedAt).toLocaleString('en-AU') : 'Date: __________________'}</div>
        </div>
      </div>

      <div className="footer">
        <span>SP Radiology · Image-Guided Procedure Consent</span>
        <span>{template.title} · v{template.version}</span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Submission detail — used for print / PDF export of a completed consent
// ────────────────────────────────────────────────────────────────────────────
export function ConsentSubmissionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [radName, setRadName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/consents/${id}`, { withCredentials: true });
      setData(r.data);
      setRadName(r.data.submission.radiologist_name || r.data.submission.countersigned_by?.name || '');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Not found');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (err) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">{err}</p></div>;
  if (!data) return <div className="min-h-screen bg-background"><Header /><p className="p-10 text-center text-sm text-muted-foreground">Loading…</p></div>;

  const s = data.submission;
  const t = data.template;

  const print = () => window.print();
  const countersign = async () => {
    if (!radName.trim()) { alert('Enter radiologist name first.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/consents/${id}/countersign`, { radiologist_name: radName.trim(), note }, { withCredentials: true });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Countersign failed'); }
    setBusy(false);
  };
  const onDelete = async () => {
    if (!window.confirm('Delete this signed consent permanently?')) return;
    try {
      await axios.delete(`${API}/consents/${id}`, { withCredentials: true });
      navigate('/consent');
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="consent-submission-page">
      <div className="print-hidden"><Header /></div>

      <div className="consent-form-wrap max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-5 print-hidden">
          <button onClick={() => navigate('/consent')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <button onClick={print} data-testid="consent-print"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: C.color }}>
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Admin countersign panel (above the doc, not printed) */}
        {isPM && s.status !== 'countersigned' && (
          <div className="print-hidden mb-5 p-4 bg-card border border-border rounded-2xl" data-testid="consent-countersign-panel">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Countersign</p>
            <p className="text-xs text-muted-foreground mt-0.5">Add the witnessing radiologist / clinician name and a note for the file.</p>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 mt-2">
              <input type="text" value={radName} onChange={e => setRadName(e.target.value)}
                placeholder="Radiologist name"
                data-testid="consent-countersign-name"
                className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Note (optional)"
                data-testid="consent-countersign-note"
                className="text-sm bg-background border border-border rounded-lg px-3 py-2" />
              <button onClick={countersign} disabled={busy}
                data-testid="consent-countersign-save"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: C.color }}>
                <Stamp className="w-4 h-4" /> Countersign
              </button>
            </div>
          </div>
        )}

        {/* Status banner */}
        <div className="print-hidden mb-4 text-xs flex items-center gap-2">
          {s.status === 'countersigned' ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-semibold"
              style={{ backgroundColor: '#16A34A15', color: '#16A34A' }}>
              <CheckCircle2 className="w-3 h-3" /> Countersigned {s.countersigned_at ? `on ${new Date(s.countersigned_at).toLocaleString('en-AU')}` : ''}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-semibold"
              style={{ backgroundColor: '#F59E0B15', color: '#F59E0B' }}>
              <AlertTriangle className="w-3 h-3" /> Awaiting clinician countersign
            </span>
          )}
          {isPM && (
            <button onClick={onDelete}
              data-testid="consent-delete"
              className="ml-auto text-[10px] font-semibold px-2 py-1 rounded-md border border-border text-red-500 hover:bg-red-500/10">
              Delete
            </button>
          )}
        </div>

        <style>{PRINT_STYLES}</style>
        <ConsentDoc
          template={t}
          patient={s.patient}
          btStatus={s.blood_thinner_status}
          btCeased={s.blood_thinner_ceased_date}
          allergies={s.allergies}
          medications={s.medications}
          surgeries={s.previous_surgeries}
          diabetic={s.diabetic}
          diabeticDetails={s.diabetic_details}
          driverBrought={s.driver_brought}
          covid2w={s.covid_vaccine_last_2_weeks}
          covidNext={s.covid_vaccine_next_week}
          claustro={s.claustrophobic}
          procedureSite={s.procedure_site || ''} setProcedureSite={() => {}}
          ticks={s.consent_ticks}
          universal={s.universal_confirmation}
          signature={s.typed_signature}
          radName={s.radiologist_name}
          signedAt={s.submitted_at}
          countersignedAt={s.countersigned_at}
          countersignedBy={s.countersigned_by}
        />
      </div>
    </div>
  );
}

// ─────────────────────────── UI helpers ───────────────────────────
function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type="text" value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

// Australian Medicare card — 10 digits displayed as "XXXX XXXXX X", plus the
// 1-digit IRN (Individual Reference Number, 1–9) for the family-member line.
// Stored as "XXXX XXXXX X / N" so downstream code / prints see it formatted.
export function MedicareField({ value, onChange, testid }) {
  const parsed = parseMedicare(value);
  const [digits, setDigits] = useState(parsed.digits);
  const [irn, setIrn] = useState(parsed.irn);

  useEffect(() => {
    // Keep internal state in sync if parent resets the field (e.g. form reset).
    const p = parseMedicare(value);
    if (p.digits !== digits) setDigits(p.digits);
    if (p.irn !== irn) setIrn(p.irn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (d, i) => {
    const formatted = d.length === 0 && i.length === 0 ? '' : formatMedicare(d, i);
    onChange(formatted);
  };
  const onDigitsChange = (raw) => {
    const d = raw.replace(/\D/g, '').slice(0, 10);
    setDigits(d);
    emit(d, irn);
  };
  const onIrnChange = (raw) => {
    const i = raw.replace(/[^1-9]/g, '').slice(0, 1);
    setIrn(i);
    emit(digits, i);
  };

  const complete = digits.length === 10 && irn.length === 1;
  const partial = digits.length > 0 || irn.length > 0;
  const invalid = partial && !complete;

  return (
    <div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Medicare</span>
      <div className="mt-1 grid grid-cols-[1fr_72px] gap-2">
        <input type="text" inputMode="numeric" autoComplete="off"
          value={formatDigitsDisplay(digits)}
          onChange={e => onDigitsChange(e.target.value)}
          placeholder="XXXX XXXXX X"
          maxLength={12}
          aria-label="Medicare card number"
          data-testid={testid}
          className="w-full text-sm bg-background border rounded-lg px-3 py-2 font-mono tracking-wider"
          style={{ borderColor: invalid ? '#DC2626' : 'hsl(var(--border))' }} />
        <input type="text" inputMode="numeric" autoComplete="off"
          value={irn}
          onChange={e => onIrnChange(e.target.value)}
          placeholder="IRN"
          maxLength={1}
          aria-label="Medicare IRN (reference number, 1–9)"
          data-testid={testid ? `${testid}-irn` : undefined}
          className="w-full text-sm bg-background border rounded-lg px-3 py-2 font-mono text-center"
          style={{ borderColor: invalid ? '#DC2626' : 'hsl(var(--border))' }} />
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">
        10-digit card number + 1-digit IRN (family-line number, 1–9).
        {invalid && <span className="ml-1 font-semibold" style={{ color: '#DC2626' }}>Enter all 10 digits and the IRN.</span>}
      </p>
    </div>
  );
}
function TextArea({ label, value, onChange, testid, placeholder, rows = 2 }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}
function RadioRow({ checked, onChange, testid, label, warn }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="radio" checked={checked} onChange={onChange}
        data-testid={testid}
        className="w-4 h-4 mt-0.5" />
      <span className="text-xs" style={{ color: warn && checked ? '#DC2626' : 'hsl(var(--foreground))', fontWeight: warn && checked ? 600 : 400 }}>{label}</span>
    </label>
  );
}
function PillBtn({ active, onClick, children, testid, warn }) {
  const color = warn ? '#DC2626' : C.color;
  return (
    <button type="button" onClick={onClick}
      data-testid={testid}
      className="flex-1 text-xs font-semibold py-2 px-3 rounded-lg border transition-colors"
      style={active
        ? { backgroundColor: `${color}15`, borderColor: color, color }
        : { borderColor: 'hsl(var(--border))' }}>
      {children}
    </button>
  );
}
function YesNoRow({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-[11px] text-foreground">{label}</p>
      <div className="flex gap-2 mt-1">
        <PillBtn active={value === true} onClick={() => onChange(true)} testid={`${testid}-yes`}>Yes</PillBtn>
        <PillBtn active={value === false} onClick={() => onChange(false)} testid={`${testid}-no`}>No</PillBtn>
      </div>
    </div>
  );
}
